import React, { useEffect, useState } from "react";
import { Phone, MapPin, ChevronDown, FileText, Truck as TruckIcon, Receipt, Download, Loader2 } from "lucide-react";
import api, { formatPrice, formatApiErrorDetail } from "@/lib/api";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import SamedaySection from "@/pages/admin/SamedaySection";
import { toast } from "sonner";

const statuses = ["nou", "confirmat", "expediat", "finalizat", "anulat"];
const statusLabel = { nou: "Nou", confirmat: "Confirmat", expediat: "Expediat", finalizat: "Finalizat", anulat: "Anulat" };
const statusStyles = {
  nou: "bg-sky-100 text-sky-700",
  confirmat: "bg-indigo-100 text-indigo-700",
  expediat: "bg-amber-100 text-amber-700",
  finalizat: "bg-emerald-100 text-emerald-700",
  anulat: "bg-red-100 text-red-700",
};

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [filter, setFilter] = useState("toate");
  const [sbIssuing, setSbIssuing] = useState(null);
  const token = localStorage.getItem("mimaso_token");
  const BACKEND = process.env.REACT_APP_BACKEND_URL;

  const load = () => api.get("/orders").then((res) => setOrders(res.data));
  useEffect(() => { load(); }, []);

  const issueSmartbill = async (o) => {
    if (!window.confirm("Emiți factura fiscală reală în SmartBill pentru această comandă?")) return;
    setSbIssuing(o._id);
    try {
      const res = await api.post(`/orders/${o._id}/smartbill-invoice`);
      setOrders((prev) => prev.map((x) => (x._id === o._id ? { ...x, ...res.data } : x)));
      const sb = res.data.smartbill || {};
      toast.success(`Factură emisă: ${sb.series} ${sb.number}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSbIssuing(null);
    }
  };

  const changeStatus = async (id, status) => {
    try {
      await api.put(`/orders/${id}/status`, { status });
      setOrders((prev) => prev.map((o) => (o._id === id ? { ...o, status } : o)));
      toast.success("Status actualizat");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const filtered = filter === "toate" ? orders : orders.filter((o) => o.status === filter);

  return (
    <div data-testid="admin-orders">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-black tracking-tight text-slate-900">Comenzi</h1>
          <p className="mt-1 text-slate-500">{orders.length} comenzi în total</p>
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-44" data-testid="orders-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="toate">Toate statusurile</SelectItem>
            {statuses.map((s) => <SelectItem key={s} value={s}>{statusLabel[s]}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {filtered.length === 0 ? (
        <div className="mt-8 rounded-2xl border border-dashed border-slate-200 py-16 text-center text-slate-500">
          Nicio comandă de afișat.
        </div>
      ) : (
        <div className="mt-8 space-y-4">
          {filtered.map((o) => (
            <div key={o._id} className="overflow-hidden rounded-2xl border border-slate-200 bg-white" data-testid={`admin-order-${o._id}`}>
              <button onClick={() => setExpanded(expanded === o._id ? null : o._id)} className="flex w-full flex-col gap-3 p-5 text-left sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
                <div className="flex flex-1 flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
                  <div>
                    <p className="font-display font-bold text-slate-900">#{o._id.slice(-6).toUpperCase()}</p>
                    <p className="text-xs text-slate-400" data-testid={`order-date-${o._id}`}>
                      {o.created_at ? new Date(o.created_at).toLocaleString("ro-RO") : "—"}
                    </p>
                  </div>
                  <div>
                    <p className="font-semibold text-slate-800" data-testid={`order-customer-name-${o._id}`}>{o.customer.name}</p>
                    <a
                      href={`tel:${o.customer.phone}`}
                      onClick={(e) => e.stopPropagation()}
                      className="inline-flex items-center gap-1.5 text-sm font-medium text-sky-600 hover:text-sky-500"
                      data-testid={`order-customer-phone-${o._id}`}
                    >
                      <Phone className="h-3.5 w-3.5" /> {o.customer.phone}
                    </a>
                  </div>
                </div>
                <div className="flex items-center justify-between gap-3 sm:justify-end">
                  <span className="font-display font-black text-slate-900">{formatPrice(o.total)}</span>
                  <span className={`rounded-full px-3 py-1 text-xs font-bold ${statusStyles[o.status]}`}>{statusLabel[o.status]}</span>
                  <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${expanded === o._id ? "rotate-180" : ""}`} />
                </div>
              </button>

              {expanded === o._id && (
                <div className="border-t border-slate-100 bg-slate-50/50 p-5">
                  <div className="grid gap-6 md:grid-cols-2">
                    <div>
                      <h4 className="label-caps mb-2 text-slate-400">Produse</h4>
                      <div className="space-y-1.5">
                        {o.items.map((it, idx) => (
                          <div key={idx} className="flex justify-between text-sm text-slate-700">
                            <span>{it.name} × {it.quantity}</span>
                            <span className="font-semibold">{formatPrice(it.price * it.quantity)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div>
                      <h4 className="label-caps mb-2 text-slate-400">Livrare</h4>
                      <p className="flex items-center gap-1.5 text-sm text-slate-700"><Phone className="h-3.5 w-3.5 text-slate-400" /> {o.customer.phone}</p>
                      {o.customer.email && <p className="text-sm text-slate-700">{o.customer.email}</p>}
                      <p className="mt-1 flex items-start gap-1.5 text-sm text-slate-700">
                        <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" />
                        {o.customer.address}, {o.customer.city}, {o.customer.county}
                      </p>
                      {o.customer.notes && <p className="mt-2 rounded-lg bg-amber-50 p-2 text-sm text-amber-800">Notă: {o.customer.notes}</p>}
                    </div>
                  </div>
                  <div className="mt-5 flex flex-wrap items-center gap-3 border-t border-slate-200 pt-4">
                    <span className="text-sm font-semibold text-slate-600">Schimbă status:</span>
                    <Select value={o.status} onValueChange={(v) => changeStatus(o._id, v)}>
                      <SelectTrigger className="w-44" data-testid={`order-status-select-${o._id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {statuses.map((s) => <SelectItem key={s} value={s}>{statusLabel[s]}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <div className="flex-1" />
                    <button
                      onClick={() => window.open(`/admin/document/factura/${o._id}`, "_blank")}
                      className="inline-flex items-center gap-2 rounded-full bg-sky-600 px-4 py-2 text-sm font-semibold text-white hover:bg-sky-500"
                      data-testid={`order-invoice-${o._id}`}
                    >
                      <FileText className="h-4 w-4" /> Factură
                      {o.invoice_number && <span className="opacity-80">· {o.invoice_number}</span>}
                    </button>
                    <button
                      onClick={() => window.open(`/admin/document/awb/${o._id}`, "_blank")}
                      className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-700"
                      data-testid={`order-awb-${o._id}`}
                    >
                      <TruckIcon className="h-4 w-4" /> AWB intern
                      {o.awb_number && <span className="opacity-80">· {o.awb_number}</span>}
                    </button>
                  </div>
                  <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-slate-200 pt-4">
                    <span className="text-sm font-semibold text-slate-600">Curier Sameday:</span>
                    <SamedaySection
                      order={o}
                      onUpdated={(updated) => setOrders((prev) => prev.map((x) => (x._id === updated._id ? { ...x, ...updated } : x)))}
                    />
                  </div>
                  <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-slate-200 pt-4">
                    <span className="text-sm font-semibold text-slate-600">Factură fiscală SmartBill:</span>
                    {o.smartbill?.number ? (
                      <>
                        <span className="inline-flex items-center gap-2 rounded-full bg-emerald-100 px-3 py-1.5 text-sm font-bold text-emerald-700" data-testid={`smartbill-issued-${o._id}`}>
                          <Receipt className="h-4 w-4" /> {o.smartbill.series} {o.smartbill.number}
                        </span>
                        <a
                          href={`${BACKEND}/api/orders/${o._id}/smartbill-invoice/pdf?token=${token}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 rounded-full bg-sky-600 px-4 py-2 text-sm font-semibold text-white hover:bg-sky-500"
                          data-testid={`smartbill-pdf-${o._id}`}
                        >
                          <Download className="h-4 w-4" /> Descarcă PDF
                        </a>
                      </>
                    ) : (
                      <button
                        onClick={() => issueSmartbill(o)}
                        disabled={sbIssuing === o._id}
                        className="inline-flex items-center gap-2 rounded-full bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-500 disabled:opacity-60"
                        data-testid={`smartbill-issue-${o._id}`}
                      >
                        {sbIssuing === o._id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Receipt className="h-4 w-4" />}
                        {sbIssuing === o._id ? "Se emite..." : "Emite factură SmartBill"}
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
